import type { CivilDate } from '../../shared/date/civil.js'
import type { PricePoint, PriceSource } from './product.js'
import type { MilliCents } from './unit.js'

/**
 * A inteligência sobre **dado próprio** da
 * `Docs/05-Plano/Fase 10 - Inteligência de preços.md` — a metade que não depende
 * de fonte externa nenhuma.
 *
 * ## A regra da fase, e como ela é cumprida aqui
 *
 * *"Não sugerir decisão de compra sem base. 'Vale a pena comprar agora' só
 * aparece com histórico suficiente para a afirmação se sustentar. Um alerta
 * errado custa mais confiança do que dez alertas certos ganham."*
 *
 * Toda função deste módulo devolve `null` quando não há base — e `null` é
 * diferente de zero, de "estável" e de "sem queda". *"O app é honesto sobre não
 * saber: sem dado suficiente, não inventa média."*
 *
 * ## A comparação é SEMPRE por unidade base
 *
 * Primeira linha da definição de pronto. Todo número daqui é
 * `pricePerBaseUnitMilliCents` — nunca `totalCents`, que compara 500ml com 1L e
 * responde errado com convicção. É a pergunta que justifica o projeto inteiro.
 *
 * ## Ponto superseded sai de tudo
 *
 * Corrigir um ponto cria outro apontando para o errado. O errado fica no banco —
 * nunca se reescreve o passado —, mas não entra em conta nenhuma: senão a
 * correção **piora** o número em vez de consertar. Mesma exclusão de
 * `recomputeStats`, e por isso ela mora numa função só, usada pelas duas.
 */

/**
 * Os pontos que valem: sem os corrigidos e sem os anulados.
 *
 * As duas exclusões são a mesma ideia por caminhos diferentes — *"o errado
 * continua no banco, mas não entra em soma nenhuma"*. Corrigido é o que ganhou
 * um substituto; anulado é o que nunca vai ganhar, porque quem anulou não
 * tinha o preço certo, só sabia que aquele estava errado.
 */
export function validPoints(points: PricePoint[]): PricePoint[] {
  const superseded = new Set(points.map((p) => p.supersedesId).filter((id) => id !== null))
  // `?? null` e não `=== null` direto: as Functions montam `PricePoint` com
  // `as unknown as PricePoint` sobre o dado cru do Firestore, **sem passar por
  // conversor**. Num ponto gravado antes da decisão 199 a chave simplesmente
  // não existe, e `undefined === null` é falso — o que faria TODO ponto antigo
  // contar como anulado e zerar as estatísticas de todos os produtos no
  // primeiro write depois do deploy. O teste de gatilho pegou isso.
  return points.filter((p) => !superseded.has(p.id) && (p.voidedAt ?? null) === null)
}

/**
 * Os pontos **vencidos**: os que uma leitura mais nova da MESMA loja substituiu.
 *
 * ## O terceiro tipo de "não conta", e ele é diferente dos outros dois
 *
 * Corrigido (`supersedesId`) é *aquele número estava errado*. Anulado
 * (`voidedAt`) é *aquele número não vale e não tenho o certo*. Este é outra
 * coisa: o número **estava certo no dia**, e simplesmente não é mais o preço de
 * lá. Um OMO a R$ 8,65/L visto na Amazon em 04/09 não deixou de ser verdade
 * sobre 04/09 — deixou de ser verdade sobre hoje, quando a mesma Amazon foi
 * conferida e estava R$ 11,79/L.
 *
 * Por isso ele **não entra em `validPoints`**: o ponto continua válido, continua
 * no histórico e continua contando como compra, se foi uma. O que ele perde é o
 * direito de responder *"onde está mais barato"*, que é uma pergunta sobre o
 * presente.
 *
 * ## `merchantId: null` NÃO vence ninguém
 *
 * Sem loja não é "a mesma loja". Dois lançamentos manuais sem lugar podem ser de
 * dois mercados diferentes, e deixar o mais novo derrubar o mais velho apagaria
 * histórico legítimo por uma semelhança que não existe.
 *
 * ## Dia POSTERIOR, e não `createdAt` — dois do mesmo dia valem os dois
 *
 * A primeira versão desempatava pela ordem de gravação, e isso está errado: dois
 * preços do mesmo dia na mesma loja são **simultâneos**, não um mais novo que o
 * outro. O caso é real e está num recado da casa — Amazon em 09/09 com R$ 11,79/L
 * no Programe e Poupe e R$ 12,41/L avulso. Deixar a ordem de digitação decidir
 * qual deles "venceu" descartaria o mais barato metade das vezes, por acaso.
 *
 * Então só vence quem tem `observedAt` **estritamente maior**. Do mesmo dia,
 * ficam todos, e a comparação escolhe o menor entre eles — que é o que alguém
 * pagaria indo lá naquele dia.
 */
export function vencidos(points: PricePoint[]): Set<string> {
  const porLoja = new Map<string, PricePoint[]>()
  for (const p of validPoints(points)) {
    if (p.merchantId === null) continue
    porLoja.set(p.merchantId, [...(porLoja.get(p.merchantId) ?? []), p])
  }

  const ids = new Set<string>()
  for (const doGrupo of porLoja.values()) {
    const maisRecente = doGrupo.reduce(
      (topo, p) => (p.observedAt > topo ? p.observedAt : topo),
      doGrupo[0]?.observedAt ?? '',
    )
    for (const p of doGrupo) {
      if (p.observedAt < maisRecente) ids.add(p.id)
    }
  }
  return ids
}

/** Os que ainda descrevem o preço de hoje. Ver `vencidos`. */
export function vigentes(points: PricePoint[]): PricePoint[] {
  const fora = vencidos(points)
  return validPoints(points).filter((p) => !fora.has(p.id))
}

/** Ordena do mais antigo ao mais novo, com `createdAt` desempatando. */
function porData(points: PricePoint[]): PricePoint[] {
  return [...points].sort(
    (a, b) => a.observedAt.localeCompare(b.observedAt) || a.createdAt.localeCompare(b.createdAt),
  )
}

/**
 * Quantos pontos são "base suficiente".
 *
 * **Quatro**, e o número tem motivo: com três, um ponto isolado é 33% da
 * amostra, e a "tendência" de um produto comprado três vezes é ruído com nome de
 * conclusão. Com quatro dá para partir a série em duas metades de dois e
 * comparar — que é o que `trend` faz.
 */
export const MINIMO_PARA_AFIRMAR = 4

export type Trend = {
  /** `'up'`, `'down'` ou `'flat'`. */
  direction: 'up' | 'down' | 'flat'
  /** Variação percentual da metade recente sobre a antiga. Com sinal. */
  percent: number
  older: MilliCents
  recent: MilliCents
}

/**
 * A tendência: a metade recente contra a metade antiga.
 *
 * `null` sem `MINIMO_PARA_AFIRMAR` pontos — ver o cabeçalho.
 *
 * **Duas metades, e não "o último contra o primeiro".** O último preço pode ser
 * uma promoção, e o primeiro pode ter sido o dia em que se pagou caro por
 * pressa; a comparação ponta a ponta transforma esses dois acidentes na
 * conclusão inteira. A média de cada metade dilui os dois.
 *
 * **Só compras.** Preço visto na gôndola não é dinheiro que saiu, e uma
 * tendência que misturasse os dois descreveria o que se viu, não o que se
 * pagou. Mesma separação de `recomputeStats`.
 *
 * A faixa morta de 5% existe porque preço de mercado oscila centavos toda
 * semana: sem ela, "subindo" e "descendo" alternariam a cada compra e a palavra
 * perderia o sentido.
 */
export const FAIXA_MORTA = 0.05

export function trend(points: PricePoint[]): Trend | null {
  const compras = porData(validPoints(points).filter((p) => p.source === 'purchase'))
  if (compras.length < MINIMO_PARA_AFIRMAR) return null

  const meio = Math.floor(compras.length / 2)
  const antigos = compras.slice(0, meio)
  const recentes = compras.slice(compras.length - meio)

  const older = media(antigos)
  const recent = media(recentes)
  if (older === 0) return null

  const percent = (recent - older) / older
  return {
    direction: Math.abs(percent) < FAIXA_MORTA ? 'flat' : percent > 0 ? 'up' : 'down',
    percent,
    older,
    recent,
  }
}

function media(points: PricePoint[]): MilliCents {
  if (points.length === 0) return 0
  return Math.round(
    points.reduce((soma, p) => soma + p.pricePerBaseUnitMilliCents, 0) / points.length,
  )
}

export type MerchantPrice = {
  /** `null` é "não sabemos onde" — e ele aparece, em vez de sumir. */
  merchantId: string | null
  /**
   * O preço de HOJE naquela loja — o mais recente, e é por ele que a lista
   * ordena. Era `cheapest` que ocupava esse lugar, e ele respondia com um preço
   * que podia não existir mais: uma Amazon com R$ 8,65/L em 04/09 e R$ 11,79/L
   * em 09/09 aparecia como a mais barata pelo número de cinco dias antes.
   */
  current: MilliCents
  currentAt: CivilDate
  /** O menor JÁ VISTO ali. História, e não oferta — ver `current`. */
  cheapest: MilliCents
  average: MilliCents
  count: number
  lastAt: CivilDate
}

/**
 * Onde este produto costuma ser mais barato.
 *
 * **`merchantId: null` vira uma linha própria**, e não é descartado: metade dos
 * pontos vem de lançamento manual sem loja, e sumir com eles faria a comparação
 * descrever um pedaço do histórico enquanto parece descrever o todo.
 *
 * Ordena pelo **mais barato**, e o empate desempata pela chave, para a ordem não
 * mudar entre leituras.
 */
export function byMerchant(points: PricePoint[]): MerchantPrice[] {
  const grupos = new Map<string | null, PricePoint[]>()
  for (const p of validPoints(points)) {
    grupos.set(p.merchantId, [...(grupos.get(p.merchantId) ?? []), p])
  }

  return [...grupos.entries()]
    .map(([merchantId, doGrupo]) => {
      const ordenados = porData(doGrupo)
      const atual = ordenados[ordenados.length - 1]
      return {
        merchantId,
        current: atual?.pricePerBaseUnitMilliCents ?? 0,
        currentAt: atual?.observedAt ?? '',
        cheapest: Math.min(...doGrupo.map((p) => p.pricePerBaseUnitMilliCents)),
        average: media(doGrupo),
        count: doGrupo.length,
        lastAt: atual?.observedAt ?? '',
      }
    })
    .sort((a, b) => a.current - b.current || (a.merchantId ?? '').localeCompare(b.merchantId ?? ''))
}

export type PriceDrop = {
  /** O ponto que caiu. */
  point: PricePoint
  /** A referência contra a qual ele caiu: a média das compras anteriores. */
  baselineMilliCents: MilliCents
  /** Quanto abaixo, entre 0 e 1. */
  percent: number
}

/**
 * O limiar de queda. **15%**, e o número é uma escolha de produto: abaixo disso é
 * oscilação de gôndola, e um alerta por oscilação é o alerta que ensina a
 * ignorar os próximos.
 */
export const QUEDA_MINIMA = 0.15

/**
 * `ProductPriceDropped`: este ponto novo está abaixo do padrão histórico?
 *
 * `null` quando não há padrão — menos de `MINIMO_PARA_AFIRMAR` pontos
 * anteriores, ou queda menor que `QUEDA_MINIMA`. É a regra da fase aplicada
 * literalmente: sem base, não afirma.
 *
 * ## A referência exclui o próprio ponto, e isso não é detalhe
 *
 * A média é das compras **anteriores** a ele. Incluí-lo puxaria a referência
 * para baixo junto com a queda, e um preço 20% menor apareceria como 16% menor —
 * o alerta some perto do limiar, que é justamente onde ele decide.
 *
 * ## A referência é de COMPRAS, e o ponto novo pode ser observado
 *
 * "Está mais barato do que a gente costuma pagar" é a frase, e o "costuma pagar"
 * só pode sair de compras. O ponto novo, esse, pode ser um preço visto na
 * gôndola — é exatamente o caso mais útil: *"o detergente Ypê está 18% mais
 * barato"* antes de comprar.
 */
export function priceDrop(novo: PricePoint, anteriores: PricePoint[]): PriceDrop | null {
  const base = validPoints(anteriores).filter(
    (p) => p.id !== novo.id && p.source === 'purchase' && p.observedAt <= novo.observedAt,
  )
  if (base.length < MINIMO_PARA_AFIRMAR) return null

  const baseline = media(base)
  if (baseline === 0) return null

  const percent = (baseline - novo.pricePerBaseUnitMilliCents) / baseline
  if (percent < QUEDA_MINIMA) return null

  return { point: novo, baselineMilliCents: baseline, percent }
}

/** O rótulo de uma origem, para a tela dizer de onde o preço veio. */
export const SOURCE_LABEL: Record<PriceSource, string> = {
  purchase: 'pago',
  manual: 'visto',
  nota_parana: 'Nota Paraná',
  web: 'internet',
}
